import { useQuery } from "@tanstack/react-query";

import { useMemo } from "react";

import { formatStaffName } from "@/lib/format-data";

import { ProviderWithServices } from "@/types/event";
import type { FirebaseProviderSummary } from "@/types/provider";

import { providerQueryOptions } from "@/api/provider/admin/provider-query";
import { serviceQueryOptions } from "@/api/provider/admin/service-query";

interface UseProvidersOptions {
  locationId?: string;
  hasFirebaseUid?: boolean;
  permission?: string;
  queryOptions?: Omit<
    typeof providerQueryOptions.list,
    "queryKey" | "queryFn" | "select"
  >;
  showArchived?: boolean;
}

// Shared so an unloaded list is the same array on every render.
const NO_PROVIDERS: FirebaseProviderSummary[] = [];

function buildProviderMap(
  providers: FirebaseProviderSummary[],
): Map<string, FirebaseProviderSummary> {
  return new Map(providers.map((p) => [p.id, p]));
}

export function useProviders(options: UseProvidersOptions = {}) {
  const {
    locationId,
    hasFirebaseUid,
    permission,
    showArchived = false,
    queryOptions,
  } = options;

  const completeOptions = {
    ...providerQueryOptions.list(locationId, permission),
    select: (data: FirebaseProviderSummary[] | null) =>
      data?.filter(
        (p) =>
          (showArchived || !p.isArchived) && (!hasFirebaseUid || p.firebaseUid),
      ) ?? [],
    ...queryOptions,
  };
  const query = useQuery(completeOptions);

  const providerIdProviderMap = useMemo(() => {
    return buildProviderMap(query.data ?? []);
  }, [query.data]);

  return useMemo(
    () => ({
      providers: query.data ?? NO_PROVIDERS,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isFetched: query.isFetched,
      error: query.error,
      refetch: query.refetch,
      providerIdProviderMap,
    }),
    [
      query.data,
      query.isLoading,
      query.isFetching,
      query.isFetched,
      query.error,
      query.refetch,
      providerIdProviderMap,
    ],
  );
}

export function useProvidersWithServices(options: UseProvidersOptions = {}) {
  const { providers, ...rest } = useProviders({
    ...options,
  });

  const { data: servicesData } = useQuery(serviceQueryOptions.list());

  const providersWithServices = useMemo(() => {
    if (!servicesData) return [];
    return providers
      .map((p) => ({
        ...p,
        services: servicesData.filter(
          (s) =>
            (s.effectiveProviderIds ?? s.providerIds).includes(p.id) ||
            s.prepProviderIds?.includes(p.id),
        ),
      }))
      .filter(
        (p) => (options.showArchived && p.isArchived) || p.services.length > 0,
      );
  }, [providers, servicesData, options.showArchived]) as ProviderWithServices[];

  return useMemo(
    () => ({
      providersWithServices,
      ...rest,
    }),
    [providersWithServices, rest],
  );
}

/**
 * Staff who can be credited as Sold by on a new sale: active, and not turned
 * off with "Can be selected as Sold by", sorted by name. `providerIdProviderMap`
 * keeps everyone, archived and hidden included, so a sale already credited to
 * someone can still show their name. Nobody counts as a seller until the list
 * has loaded: while `isReady` is false, `isSoldByCandidate` is false for all.
 */
export function useSoldByProviders() {
  const { providers, providerIdProviderMap, isLoading, error } = useProviders({
    showArchived: true,
  });
  const isReady = !isLoading && !error;

  const soldByProviders = useMemo(
    () =>
      providers
        .filter((p) => !p.isArchived && p.soldByEligible !== false)
        .sort((a, b) => formatStaffName(a).localeCompare(formatStaffName(b))),
    [providers],
  );

  const isSoldByCandidate = useMemo(() => {
    const ids = new Set(soldByProviders.map((p) => p.id));
    return (providerId: string | null | undefined) =>
      isReady && !!providerId && ids.has(providerId);
  }, [soldByProviders, isReady]);

  return useMemo(
    () => ({
      soldByProviders,
      isSoldByCandidate,
      providerIdProviderMap,
      isReady,
      isLoading,
      error,
    }),
    [
      soldByProviders,
      isSoldByCandidate,
      providerIdProviderMap,
      isReady,
      isLoading,
      error,
    ],
  );
}
